import _ from 'lodash';
import React, { useState } from 'react';
import styled from 'styled-components';

import { mediaQueries } from 'shared/styles';
import { Select, Image, SkeletonLoader } from 'shared/components';
import DiscountBadge from 'shared/components/discount-badge/discount-badge';
import { useSharedPaths } from 'shared/hooks/use-shared-paths';

import { Typography } from 'src/typography';
import { Quantity } from 'src/components/quantity';
import { useHideDiscountFromConsumer } from 'shared/hooks/use-hide-discounts-from-consumer';
import { useHideSalesLanguage } from 'src/hooks/use-hide-sales-language';
import { usePriceCartCalcs } from 'src/hooks/use-price-cart-calcs';
import { useCartPricing } from 'hooks/use-cart-pricing/use-cart-pricing';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { ProductDetails } from './product-details';

export default function SummaryItem(props) {
  const {
    additionalOption,
    onQuantityChange,
    onRemoveItem,
    formattedOption,
    option,
    cartItem,
    product,
    dispensary,
    quantity = 0,
    quantitiesAvailableForSelection,
    brandName,
    receiptItem,
    isCalculating,
    isDemo,
    isEmbedded,
    isKiosk,
    isStoreFront,
    isMobileEcommApp,
    discountToCartApplied,
    totalQuantityAvailable,
  } = props;

  const { href } = useSharedPaths({ product, dispensary, isEmbedded, isDemo, isKiosk, isStoreFront });
  const handleSelectChange = (event) => onQuantityChange(event.target.value);
  const selectStyle = { lineHeight: '1.3', height: '28px', width: '53px', paddingRight: '24px' };

  const productStringParts = [product.Name, formattedOption, additionalOption];
  const productString = _.compact(productStringParts).join(' | ');
  const hideDiscountsEnabled = useHideDiscountFromConsumer(dispensary);
  const hideSalesLanguage = useHideSalesLanguage(dispensary?.location?.state);
  const hideDiscounts = isCalculating || hideDiscountsEnabled || hideSalesLanguage;
  const priceCartEnabled = usePriceCartCalcs();
  const { getItemPrice } = useCartPricing();
  const flags = useFlags();
  const consolidateReceiptDiscountsEnabled = flags['core.cats-ecomm.consolidate-receipt-discounts.rollout'] ?? false;

  const receiptItemPrices = getItemPrice(cartItem);
  const { originalPrice, bogoDiscountedPrice } = receiptItemPrices;
  const discountedPrice = receiptItemPrices.adjustedPrice;

  const onlyComparePrices = consolidateReceiptDiscountsEnabled && priceCartEnabled;
  const isWeightOnSale = onlyComparePrices
    ? Number(discountedPrice ?? originalPrice) < Number(originalPrice)
    : !_.isEmpty(receiptItem?.discounts?.sale) || !_.isEmpty(receiptItem?.discounts?.SALE);

  const showDiscountedPrice =
    (!!bogoDiscountedPrice && (!discountToCartApplied || hideDiscounts || hideSalesLanguage)) || !!isWeightOnSale;

  const isQuantityLimitMaxAvailableEnabled = dispensary?.storeSettings?.quantityLimitMaxAvailable ?? false;
  const [hasQuantityError, setHasQuantityError] = useState(false);

  return (
    <Container>
      {!hideDiscounts && (
        <>
          {bogoDiscountedPrice &&
            // TODO: Ask if we actually want this check, it hides all offers unless a DTC isn't there
            !discountToCartApplied && (
              <DiscountBadge
                type='bogo'
                originalPrice={originalPrice}
                discountedPrice={discountedPrice}
                item={cartItem}
                isMobile={isMobileEcommApp}
                forceDollarFormat={priceCartEnabled}
              />
            )}
          {!bogoDiscountedPrice && isWeightOnSale && (
            <DiscountBadge
              type='sale'
              originalPrice={originalPrice}
              discountedPrice={discountedPrice}
              item={cartItem}
              option={option}
              forceDollarFormat={priceCartEnabled}
            />
          )}
        </>
      )}
      <StandardDetails>
        <ProductDetails
          brandName={brandName}
          href={href}
          isMobileEcommApp={isMobileEcommApp}
          productString={productString}
        />

        {isQuantityLimitMaxAvailableEnabled ? (
          <>
            <Quantity
              onQuantityChange={handleSelectChange}
              quantity={quantity}
              totalQuantityAvailable={totalQuantityAvailable}
              setHasQuantityError={setHasQuantityError}
            />
          </>
        ) : (
          <SelectContainer>
            <Select
              aria-label='Change Quantity'
              bg='white'
              onChange={handleSelectChange}
              pr='24px'
              right='8px'
              style={selectStyle}
              value={parseInt(quantity, 10)}
              data-testid='summary-quantity-select'
            >
              {_.map(quantitiesAvailableForSelection, (num) => (
                <option key={num} value={num}>
                  {num}
                </option>
              ))}
            </Select>
          </SelectContainer>
        )}

        {isCalculating ? (
          <SkeletonLoader height='28px' width='70px' />
        ) : (
          <Price data-testid='summary-price' showDiscountedPrice={showDiscountedPrice && !hideDiscounts}>
            {(!hideDiscounts || !showDiscountedPrice) && <div>${originalPrice}</div>}
            {showDiscountedPrice && <div>${discountedPrice}</div>}
          </Price>
        )}

        <RemoveButton aria-label='Remove selection' onClick={onRemoveItem}>
          <Image src='/icons/x-grey.svg' alt='' width='9px' height='9px' />
        </RemoveButton>
      </StandardDetails>

      {hasQuantityError && (
        <QuantityError tag='div' size='small'>
          {totalQuantityAvailable} max quantity available
        </QuantityError>
      )}
    </Container>
  );
}

const Container = styled.div`
  border-bottom: 1px solid #e4e8ea;
  padding: 12px 0 13px 0;
  max-width: 390px;
`;

const SelectContainer = styled.div`
  flex: 0 0 49px;
  height: 28px;
  min-width: 0;
`;

const Price = styled.div`
  color: #5d666d;
  flex: 0 0 70px;
  font-size: 13px;
  font-weight: bold;
  text-align: right;
  min-width: 0;

  ${({ showDiscountedPrice }) =>
    showDiscountedPrice &&
    `
    > div:first-child {
      font-weight: normal;
      margin-top: 2px;
      text-decoration: line-through;
    }
  `}
`;

const RemoveButton = styled.button`
  appearance: none;
  border: none;
  padding: 0;
  background-color: transparent;
  cursor: pointer;
  flex: 0 0 9px;
  height: 11px;
  margin-left: 20px;
  margin-right: 16px;
  display: flex;
  @media ${mediaQueries.largePhone} {
    margin-left: 12px;
  }
`;

const StandardDetails = styled.div`
  align-items: center;
  display: flex;
`;

const QuantityError = styled(Typography.Body)`
  color: ${({ theme }) => theme.colors.red[45]};
  margin: 4px 0 0 0;
  text-align: right;
`;
